import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminShared.css";
import { API_BASE_URL } from "../config";

const STATUS_OPTIONS = ["New", "Contacted", "Closed"];

export default function AdminPartners() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});

  /* ================= AUTH + FETCH ================= */
  useEffect(() => {
    const adminInfo = localStorage.getItem("adminInfo");
    if (!adminInfo) return navigate("/admin");
    fetchPartners();
  }, [navigate]);

  /* ================= FETCH PARTNERS ================= */
  const fetchPartners = async () => {
    try {
      setLoading(true);
      const token = JSON.parse(localStorage.getItem("adminInfo"))?.token;
      const res = await fetch(`${API_BASE_URL}/adminpatnertouch/admin`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      setPartners(
        (result.data || []).sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        ),
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /* ================= READ MORE ================= */
  const toggleReadMore = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  /* ================= DELETE ================= */
  const handleDelete = async (id) => {
    if (!window.confirm("Purge this partner request?")) return;
    try {
      const token = JSON.parse(localStorage.getItem("adminInfo"))?.token;
      await fetch(`${API_BASE_URL}/adminpatnertouch/admin/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchPartners();
    } catch (err) {
      console.error(err);
    }
  };

  /* ================= STATUS UPDATE ================= */
  const handleStatusChange = async (id, status) => {
    try {
      const token = JSON.parse(localStorage.getItem("adminInfo"))?.token;
      await fetch(`${API_BASE_URL}/adminpatnertouch/admin/${id}/status`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status }),
      });
      setPartners((prev) =>
        prev.map((p) => (p._id === id ? { ...p, status } : p)),
      );
    } catch (err) {
      console.error(err);
    }
  };

  if (loading)
    return (
      <div className="admin-list-container">
        <p style={{ padding: "100px", textAlign: "center" }}>
          ⏳ Syncing Partner Leads...
        </p>
      </div>
    );

  return (
    <div className="admin-list-container">
      <header className="admin-list-header">
        <div className="header-left-group">
          <img src="/logo.png" alt="FlyTutor" className="admin-header-logo" />
          <h1 className="admin-header-title">Partnership Gateway</h1>
        </div>
        <button
          className="module-badge"
          style={{ cursor: "pointer", border: "none" }}
          onClick={fetchPartners}
        >
          Sync Leads 🔄
        </button>
      </header>

      <div className="admin-list-content">
        <div className="admin-info-bar">
          <div className="admin-user-tag">
            <div className="dot" style={{ background: "#ec4899" }}></div>
            Operator: Global Relations
          </div>
          <div
            className="admin-status-badge"
            style={{
              background: "#fdf2f8",
              color: "#9d174d",
              fontSize: "0.75rem",
              fontWeight: 800,
            }}
          >
            {partners.length} REQUESTS PENDING
          </div>
        </div>

        <div className="admin-table-wrapper">
          <table className="elite-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Proponent Details</th>
                <th>Inquiry Content</th>
                <th>Workflow Status</th>
                <th>Deployment Data</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {partners.length === 0 ? (
                <tr>
                  <td
                    colSpan="6"
                    style={{
                      textAlign: "center",
                      padding: "100px",
                      color: "#94a3b8",
                    }}
                  >
                    <div style={{ fontSize: "2rem", marginBottom: "15px" }}>
                      📦
                    </div>
                    No active partner requests in queue.
                  </td>
                </tr>
              ) : (
                partners.map((p, i) => (
                  <tr key={p._id}>
                    <td>{i + 1}</td>
                    <td>
                      <div style={{ fontWeight: 700 }}>{p.name}</div>
                      <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                        {p.email}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#64748b" }}>
                        {p.number}
                      </div>
                    </td>
                    <td style={{ maxWidth: "300px" }}>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "#475569",
                          lineHeight: "1.4",
                          display: "-webkit-box",
                          WebkitLineClamp: expandedRows[p._id] ? "unset" : "2",
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {p.message}
                      </div>
                      {p.message?.length > 80 && (
                        <button
                          onClick={() => toggleReadMore(p._id)}
                          style={{
                            background: "none",
                            border: "none",
                            color: "#6366f1",
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            cursor: "pointer",
                            padding: "4px 0",
                          }}
                        >
                          {expandedRows[p._id]
                            ? "HIDE CONTENT ↑"
                            : "VIEW COMPLETE CONTENT ↓"}
                        </button>
                      )}
                    </td>
                    <td>
                      <select
                        className={`status-tag status-${p.status?.toLowerCase()}`}
                        value={p.status}
                        onChange={(e) =>
                          handleStatusChange(p._id, e.target.value)
                        }
                        style={{
                          border: "none",
                          cursor: "pointer",
                          outline: "none",
                        }}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>
                            {s.toUpperCase()}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {new Date(p.createdAt).toLocaleDateString("en-IN")}
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#94a3b8" }}>
                        {new Date(p.createdAt).toLocaleTimeString("en-IN")}
                      </div>
                    </td>
                    <td>
                      <button
                        className="action-btn-elite btn-delete"
                        onClick={() => handleDelete(p._id)}
                      >
                        <span>🗑️</span> Purge
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
